import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { HERO_IMAGES } from "@/lib/assets";
import { ArrowDown } from "lucide-react";

const line = { hidden: { y: "110%" }, show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.2 + i * 0.15, ease: [0.16, 1, 0.3, 1] } }) };

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const lines = [t.hero.l1, t.hero.l2, t.hero.l3];

  const [bgIndex, setBgIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setBgIndex((i) => (i + 1) % HERO_IMAGES.length), 6500);
    return () => clearInterval(id);
  }, []);

  const go = (id) => () => {
    const el = document.getElementById(id);
    if (el) window.__lenis ? window.__lenis.scrollTo(el, { offset: -80 }) : el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden bg-icdh-navy">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <AnimatePresence mode="sync">
          <motion.img
            key={bgIndex}
            src={HERO_IMAGES[bgIndex]}
            alt="ICDH students"
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0 h-full w-full object-cover"
            data-testid={`hero-bg-${bgIndex}`}
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-t from-icdh-navy via-icdh-navy/60 to-icdh-navy/40" />
      </motion.div>
      <div className="noise-overlay absolute inset-0 opacity-[0.05] pointer-events-none" />

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-10 min-h-screen flex flex-col justify-end pb-24 pt-32">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          className="flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-icdh-white/70 mb-8">
          <span className="h-px w-10 bg-icdh-red" />{t.hero.overline}
        </motion.div>

        <h1 className="font-serif text-icdh-white tracking-tight text-[13vw] leading-[0.92] md:text-[7.5vw]">
          {lines.map((ln, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block">
                {i === 2 ? <span className="italic text-icdh-red">{ln}</span> : ln}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-10 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <p className="max-w-md text-icdh-white/80 text-base md:text-lg">{t.hero.sub}</p>
          <div className="flex flex-wrap gap-4">
            <button onClick={go("admissions")} data-testid="hero-cta-admission"
              className="h-14 px-8 bg-icdh-red text-icdh-white text-xs font-bold uppercase tracking-[0.15em] hover:bg-icdh-white hover:text-icdh-navy transition-colors duration-300">
              {t.hero.cta1}
            </button>
            <button onClick={go("about")} data-testid="hero-cta-discover"
              className="h-14 px-8 border border-icdh-white/40 text-icdh-white text-xs font-bold uppercase tracking-[0.15em] hover:bg-icdh-white/10 transition-colors duration-300 flex items-center gap-3">
              {t.hero.cta2} <ArrowDown size={16} />
            </button>
          </div>
        </motion.div>

        {/* Bg indicator dots */}
        <div className="mt-10 flex items-center gap-2" data-testid="hero-indicators">
          {HERO_IMAGES.map((_, i) => (
            <button
              key={i}
              onClick={() => setBgIndex(i)}
              aria-label={`Show background ${i + 1}`}
              data-testid={`hero-dot-${i}`}
              className={`h-1 transition-all duration-500 ${i === bgIndex ? "w-10 bg-icdh-red" : "w-6 bg-icdh-white/30 hover:bg-icdh-white/60"}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
